import { destroySession, getSession, SessionData, updateSession } from '@app/libs/session';
import { apiConfig } from '@app/repositories/config';

import { logger } from '../logger';

import BaseService, { IServices, ServiceMethod, HttpError } from './BaseService';

export abstract class AbstractHttp {
  protected services: IServices = {};

  protected cookies: string | null = null;
  protected serverReq: any = null;
  protected serverRes: any = null;

  protected accessToken: string | null = null;
  protected refreshToken: string | null = null;
  protected expiresAt: number | null = null;

  protected Exception401 = (req: any, res: any, errors?: any) => {
    if (!req || !res) return;
    destroySession(req, res);
    const url: string = (req as any)?.url || '';
    const isApiRoute = url.startsWith('/api/');

    if (!isApiRoute) {
      // Trong GSSP: không được ghi response ở đây, để caller xử lý
      return;
    }

    res.status?.(401).json({
      success: false,
      error: 'UNAUTHORIZED',
      message: errors?.[0]?.message?.vn || 'Phiên đăng nhập đã hết hạn. Vui lòng đăng nhập lại',
    });
    res.end?.();
  };

  setTokens(accessToken: string, refreshToken: string, expiresAt: number) {
    this.accessToken = accessToken;
    this.refreshToken = refreshToken;
    this.expiresAt = expiresAt;

    return this;
  }

  setCookies(cookies: string) {
    this.cookies = cookies;

    return this;
  }

  setServerContext(req: any, res: any) {
    this.serverReq = req;
    this.serverRes = res;

    return this;
  }

  send(
    action: keyof typeof this.services,
    options: {
      params?: any;
      headers?: any;
    }
  ) {
    const serviceConfig = this.services[action];
    const service = new BaseService(serviceConfig);
    if (service.url?.startsWith('/') && this.serverReq) {
      service.url = apiConfig.base + service.url;
    }

    const maxAttempts = serviceConfig.maxAttempts;
    const isServer = typeof window === 'undefined';
    let attempTry = 0;
    let req: any = this.serverReq;
    let res: any = this.serverRes;
    if (!req || !res) {
      try {
        // @ts-ignore: we are in API runtime
        req = globalThis.__NEXT_PRIVATE_API_REQ as any; // không có trên client
        // @ts-ignore
        res = globalThis.__NEXT_PRIVATE_API_RES as any;
      } catch {
        logger.error('Failed to get req/res for repository layer to access session');
      }
    }

    const attempt = async (withRenew: boolean): Promise<any> => {
      attempTry++;
      const headers = { ...(options.headers || {}) };
      if (isServer && serviceConfig.authRequire) {
        try {
          const session: { data: SessionData } = getSession(req) || {
            data: {
              accessToken: this.accessToken,
              refreshToken: this.refreshToken,
              expiresAt: this.expiresAt,
            } as SessionData,
          };

          const skewMs = 15 * 1000;
          const expiresAt = session.data.expiresAt || 0;
          let { accessToken, refreshToken } = session.data;
          if (serviceConfig.authRequire !== 'optional' && (!accessToken || !refreshToken)) {
            this.Exception401(req, res);

            return Promise.reject(new HttpError(res.status, {}, res.statusText));
          }

          const now = Date.now();
          const isExpiredOrNear = now >= expiresAt - skewMs;
          const needRenew = isExpiredOrNear || (!accessToken && !!refreshToken);
          if (
            serviceConfig.authRequire !== 'optional' &&
            withRenew &&
            !!refreshToken &&
            needRenew
          ) {
            try {
              const { authRepository } = await import('@app/repositories/auth.repository');
              const { data, status, errors } = await authRepository.renewToken(refreshToken);
              logger.info('Renew token', status);

              if (!status || errors) {
                logger.info('Renew token failed', errors);
                this.Exception401(req, res, errors);

                return Promise.reject(new HttpError(res.status, errors, res.statusText));
              }

              logger.info('Renew token success', data);
              updateSession(req, res, data);
              accessToken = data.accessToken;
              refreshToken = data.refreshToken;
            } catch {
              this.Exception401(req, res);

              return Promise.reject(new HttpError(res.status, {}, res.statusText));
            }
          }

          if (accessToken) {
            headers['Authorization'] = `Bearer ${accessToken}`;
          }
        } catch (e) {
          logger.error('Failed to get session for repository layer to access session', e);
          this.Exception401(req, res);

          if (isServer) {
            res.status(401).json({
              success: false,
              error: 'UNAUTHORIZED',
              message: 'Phiên đăng nhập đã hết hạn. Vui lòng đăng nhập lại',
            });
            res.end?.();
          } else {
            return Promise.reject(new HttpError(res.status, {}, res.statusText));
          }
        }
      }

      try {
        if (this.cookies) {
          headers['Cookie'] = this.cookies;
        }

        return await service.request({ ...options, headers });
      } catch (e: any) {
        if (maxAttempts && attempTry < maxAttempts) {
          logger.error('Retrying request', `${attempTry}/${maxAttempts} times`);

          return await attempt(true);
        }

        const hasTokenError = Array.isArray(e?.errors)
          ? e.errors.some((error: any) => error?.code === 'TK-0004')
          : false;
        if (e.status === 401 || hasTokenError) {
          if (isServer) this.Exception401(req, res, e.errors);

          throw e;
        }

        throw e;
      }
    };

    return attempt(true);
  }
}

export { BaseService };
export type { ServiceMethod };
